<script lang="ts" setup>
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import VMenu from '../VMenu/VMenu.vue';
import { type NavItem } from './types';

export interface Props {
  navItem: NavItem<string>;
  preserveQuery?: boolean;
}

const props = defineProps<Props>();
const route = useRoute();

const hasChildren = computed(() => Boolean(props.navItem.children.length));

// Vind het child dat matcht op basis van het pad (negeert laatste segment voor dynamische parameters)
const label = computed(() => {
  const currentPath = route.path;
  const matchingChild = props.navItem.children?.find((c) => {
    if (!c.additionalFields) return false;
    // Match op alles behalve het laatste segment (voor dynamische parameters zoals _ALGEMEEN_)
    const basePath = c.additionalFields.substring(0, c.additionalFields.lastIndexOf('/') + 1);
    return currentPath.startsWith(basePath);
  });
  return matchingChild?.label ?? props.navItem.label;
});

const menuRef = ref<InstanceType<typeof VMenu>>();

function isExternalLink(url?: string) {
  return url?.startsWith('/app/');
}

function toggle(event?: Event | undefined) {
  if (event) menuRef.value?.toggle(event);
}

const subMenuItems = computed(() =>
  props.navItem.children?.map((child) => ({
    label: child.label,
    icon: '',
    url: child.additionalFields,
  })),
);
</script>

<template>
  <RouterLink
    :to="{ path: props.navItem.additionalFields, query: preserveQuery ? route.query : undefined }"
    custom
    v-slot="{ navigate }"
  >
    <a
      class="p-[.4em] text-black hover:no-underline"
      :href="hasChildren ? undefined : props.navItem.additionalFields"
      @click="
        (e) =>
          hasChildren
            ? toggle(e)
            : isExternalLink(props.navItem.additionalFields)
              ? undefined
              : navigate(e)
      "
    >
      {{ label }}
      <FontAwesomeIcon v-if="hasChildren" icon="fa-regular fa-chevron-down" />
    </a>
    <VMenu v-if="hasChildren" ref="menuRef" :model="subMenuItems" popup>
      <template #item="{ item, props }">
        <RouterLink v-slot="{ navigate }" :to="item.url!" custom>
          <a
            :href="item.url"
            v-bind="props.action"
            @click="(e) => (isExternalLink(item.url) ? undefined : navigate(e))"
          >
            <span>{{ item.label }}</span>
          </a>
        </RouterLink>
      </template>
    </VMenu>
  </RouterLink>
</template>
